import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ContextActionType, useDumbActionsContext } from '@europrocurement/flexy-components';
import { ColumnDatatable, FiltersDatatableList } from '@europrocurement/flexy-datatable';

import { FaOptionIcon, addItem } from '@europrocurement/l2d-icons';
import { GroupementGroupementRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { IconName } from '@fortawesome/sharp-regular-svg-icons';
import { useOffreService } from '@europrocurement/l2d-domain';
import { Typography } from '@mui/material';
import { getStringColumn } from '../listings';
import RoutedListing from '../components/RoutedListing';
import useFilterOptions from '../Catalog/useFilterOptions';
import { getStaticSelectFilter } from '../components/filters';
import { PossibleTypes } from '../../Societes';
import { groupCreatePath, groupFichePath } from './routes';

const Panel: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const { offerGroupModel: model } = useOffreService();
    const filterOptions = useFilterOptions();

    const goToCreate = React.useCallback(() => navigate(groupCreatePath), [navigate]);
    const goToFiche = React.useCallback(
        (item: { id: string | number }) => navigate(groupFichePath(item.id)),
        [navigate],
    );

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer un groupement',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: goToCreate,
            },
        ],
        [goToCreate],
    );

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<GroupementGroupementRead>[] = [
        getStringColumn('libelle'),

        {
            label: 'Icône',
            displayLabel: true,
            render: ({ iconeName: iconName }) => {
                if (iconName) {
                    return (
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            <FaOptionIcon icon={['fad', iconName as IconName]} />
                        </Typography>
                    );
                }
                return null;
            },
            isDisplayed: true,
        },
        {
            isDisplayed: true,
            label: 'Rang',
            displayLabel: true,
            field: 'rang',
            sortable: true,
            render: ({ rang }) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {rang}
                </Typography>
            ),
        },
        {
            label: 'Domaine',
            displayLabel: true,
            render: ({ domaine: domain }) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {domain?.libelle}
                </Typography>
            ),
            isDisplayed: true,
            sortable: false,
            field: 'domaine',
        },
    ];

    const filters: FiltersDatatableList = [
        getStaticSelectFilter('Domaine', 'domaineId', filterOptions.domains),
    ];

    return (
        <RoutedListing
            listingTitle="Liste des groupements"
            createButton={{
                label: 'Créer un groupement',
                icon: addItem,
                onClick: goToCreate,
            }}
            listingColumns={columns as ColumnDatatable<unknown>[]}
            model={model}
            filters={filters}
            filtersControl
            searchBarType={PossibleTypes.Group}
            onRowClick={goToFiche}
        />
    );
};

export default Panel;
